import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppButton, AppLinkButton, AppShell, CenteredCard } from '@/pages/external/components/shell'
import { Avatar } from '@/pages/external/components'
import { authenticateOAuthFlow, getOAuthFlow, OAuthFlowContext } from '@/services/oauth'
import { OAuthConsent } from './OAuthConsent'
import { OAuthLoginForm } from './OAuthLoginForm'
import { OAuthError, OAuthLoading } from './OAuthStates'
import { useConnectSession } from './useConnectSession'
import { useIsFramed } from './frameGuard'

type LoadState = 'loading' | 'ready' | 'error'

/**
 * DUPR-hosted OAuth consent SPA. Route: `/oauth/authorize?flow=<flowId>`.
 *
 * The partner redirects the browser here (top-level) after `GET /oauth/v1.0/authorize`. This page
 * reads `?flow=`, loads the flow context (cookie-bound), and drives the user to a consent decision:
 *
 *   authenticated=true  → render the consent UI (identity banner + permission toggles).
 *   authenticated=false → authenticate first, then re-fetch the flow and render consent:
 *       • an existing DUPR session → one-tap "Continue as {user}" (mandatory confirmation).
 *       • otherwise → the DUPR login form (credentials go only to DUPR).
 *     Binding calls POST /oauth/v1.0/authorize/authenticate {flowId} with the session bearer token.
 *
 * CLICKJACKING: this identity-confirmation screen must never be framed. The authoritative defense
 * is an `Content-Security-Policy: frame-ancestors 'none'` + `X-Frame-Options: DENY` response header
 * that the HOST/CDN must send for this route (no per-route header mechanism exists in this static
 * SPA repo — see frameGuard.ts). `useIsFramed` is the runtime fallback that refuses to render.
 */
export function OAuthAuthorizePage() {
  const [searchParams] = useSearchParams()
  const flowId = searchParams.get('flow')

  const framed = useIsFramed()
  const { hasSession, user, isInitializing } = useConnectSession()

  const [flow, setFlow] = useState<OAuthFlowContext | null>(null)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [binding, setBinding] = useState(false)
  const [useDifferentAccount, setUseDifferentAccount] = useState(false)

  // Initial flow load.
  useEffect(() => {
    let cancelled = false
    if (!flowId) {
      setLoadState('error')
      setErrorMessage('This link is missing its connection reference. Start the connection again from your partner.')
      return
    }
    getOAuthFlow(flowId)
      .then((ctx) => {
        if (cancelled) return
        setFlow(ctx)
        setLoadState('ready')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setLoadState('error')
        setErrorMessage(
          e instanceof Error ? e.message : 'This connection request has expired. Start the connection again.'
        )
      })
    return () => {
      cancelled = true
    }
  }, [flowId])

  // Bind the authenticated user to the flow, then re-fetch context to reach the consent step.
  const bindFlow = useCallback(
    async () => {
      if (!flowId) return
      setBinding(true)
      setErrorMessage('')
      try {
        await authenticateOAuthFlow(flowId)
        const refreshed = await getOAuthFlow(flowId)
        setFlow(refreshed)
        setLoadState('ready')
      } catch (e: unknown) {
        setLoadState('error')
        setErrorMessage(
          e instanceof Error ? e.message : 'We could not confirm your account for this request. Please try again.'
        )
      } finally {
        setBinding(false)
      }
    },
    [flowId]
  )

  // Refuse to render the consent surface inside a frame (clickjacking guard). The
  // break-out link reopens this exact URL as a top-level tab so the user can finish there.
  if (framed) {
    return (
      <OAuthError
        title="Can’t continue here"
        message="For your security, DUPR sign-in can’t run inside another site. Continue in your own browser tab to finish connecting."
        action={
          <AppLinkButton href={window.location.href} target="_blank" rel="noopener noreferrer" fullWidth>
            Continue in browser
          </AppLinkButton>
        }
      />
    )
  }

  if (loadState === 'loading' || isInitializing) {
    return <OAuthLoading message="Preparing your connection…" />
  }

  if (loadState === 'error' || !flow || !flowId) {
    return <OAuthError title="Connection unavailable" message={errorMessage || 'Please start the connection again.'} />
  }

  // Already authenticated → straight to consent.
  if (flow.authenticated) {
    return <OAuthConsent flowId={flowId} context={flow} sessionUser={user} />
  }

  // Pending flow: bind in progress.
  if (binding) {
    return <OAuthLoading message="Confirming your account…" />
  }

  // Pending flow with an existing DUPR session → mandatory one-tap identity confirmation.
  if (hasSession && user && !useDifferentAccount) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <CenteredCard>
          <div className="flex flex-col items-center gap-y-4 w-full">
            <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)]">
              Connect to {flow.partnerName}
            </p>
            <Avatar src={user.imageUrl} alt={user.fullName} />
            <div className="text-center">
              <p className="text-[color:var(--ink-soft)] text-sm">Continue as</p>
              <p className="text-[color:var(--ink)] font-semibold text-lg">{user.fullName}</p>
              <p className="text-[color:var(--ink-soft)] text-sm">DUPR {user.referralCode}</p>
            </div>
            <div className="flex flex-col w-full gap-y-2 mt-2">
              <AppButton fullWidth onClick={() => bindFlow()}>
                Continue as {user.fullName?.split(' ')[0] || 'me'}
              </AppButton>
              <AppButton variant="ghost" fullWidth onClick={() => setUseDifferentAccount(true)}>
                Use a different account
              </AppButton>
            </div>
          </div>
        </CenteredCard>
      </AppShell>
    )
  }

  // Pending flow, no usable session (or the user chose a different account) → DUPR login.
  return (
    <AppShell logoLinksToDashboard={false}>
      <CenteredCard>
        <OAuthLoginForm
          partnerName={flow.partnerName}
          onAuthenticated={bindFlow}
          onBack={hasSession && user ? () => setUseDifferentAccount(false) : undefined}
        />
      </CenteredCard>
    </AppShell>
  )
}

export default OAuthAuthorizePage
